'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';

export default function USImportPermits() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el) => el.classList.remove('hidden-init'));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const items = [
    {
      key: 'iftp',
      code: 'IFTP00104977',
      status: t('compliance.usperm.status.active'),
      statusTone: 'active' as const,
      issuer: 'NOAA Fisheries — NMFS',
      icon: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z',
    },
    {
      key: 'simp',
      code: '50 CFR 300.320–300.325',
      status: t('compliance.usperm.status.framework'),
      statusTone: 'framework' as const,
      issuer: 'NOAA Fisheries — SIMP',
      icon: 'M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z',
    },
    {
      key: 'fda',
      code: '21 CFR Part 123',
      status: t('compliance.usperm.status.framework'),
      statusTone: 'framework' as const,
      issuer: 'U.S. Food and Drug Administration',
      icon: 'M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z',
    },
    {
      key: 'verify',
      code: 'mercaliacg.com/verify',
      status: t('compliance.usperm.status.online'),
      statusTone: 'online' as const,
      issuer: 'MERCALIA Connect Group LLC',
      icon: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
    },
  ];

  const tone: Record<string, { bg: string; text: string; border: string }> = {
    active: { bg: 'rgba(42,155,173,0.14)', text: '#7FE3F0', border: 'rgba(42,155,173,0.45)' },
    framework: { bg: 'rgba(255,255,255,0.07)', text: '#C6D4E2', border: 'rgba(255,255,255,0.18)' },
    online: { bg: 'rgba(201,168,76,0.14)', text: '#E3C978', border: 'rgba(201,168,76,0.4)' },
  };

  return (
    <section ref={sectionRef} className="py-24" style={{ background: 'linear-gradient(180deg, #0B2C4A 0%, #0E3A60 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <span className="section-label block mb-3 reveal hidden-init" style={{ color: '#2A9BAD' }}>
            {t('compliance.usperm.label')}
          </span>
          <h2
            className="font-display font-bold text-white reveal hidden-init stagger-1"
            style={{ fontSize: 'clamp(30px, 4vw, 50px)', letterSpacing: '-0.03em', lineHeight: '1.05' }}
          >
            {t('compliance.usperm.title1')}{' '}
            <span className="italic font-light" style={{ color: '#2A9BAD' }}>{t('compliance.usperm.titleItalic')}</span>
          </h2>
          <p
            className="text-base leading-relaxed max-w-2xl mx-auto mt-5 reveal hidden-init stagger-2"
            style={{ color: 'rgba(255,255,255,0.72)' }}
          >
            {t('compliance.usperm.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((item, i) => {
            const c = tone[item.statusTone];
            return (
              <div
                key={item.key}
                className="reveal hidden-init rounded-2xl p-7 transition-all duration-300"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(42,155,173,0.22)',
                  transitionDelay: `${i * 70}ms`,
                }}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(42,155,173,0.15)', border: '1.5px solid rgba(42,155,173,0.35)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#2A9BAD"><path d={item.icon} /></svg>
                  </div>
                  <span
                    className="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap"
                    style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
                  >
                    {item.status}
                  </span>
                </div>

                <h3 className="font-display font-bold text-white text-xl mb-2 leading-snug">
                  {t(`compliance.usperm.${item.key}.name`)}
                </h3>

                <div
                  className="font-mono text-sm mb-4 tracking-wide"
                  style={{ color: '#7FE3F0' }}
                >
                  {item.code}
                </div>

                <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {t(`compliance.usperm.${item.key}.description`)}
                </p>

                <div className="pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <span className="text-xs uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {t('compliance.usperm.issuer')}
                  </span>
                  <div className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>{item.issuer}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center reveal hidden-init">
          <Link
            href="/verify"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm transition-all duration-300"
            style={{ background: '#2A9BAD', color: '#04182B' }}
          >
            {t('compliance.usperm.cta')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" /></svg>
          </Link>
          <p className="text-xs max-w-3xl mx-auto mt-7 leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {t('compliance.usperm.note')}
          </p>
        </div>
      </div>
    </section>
  );
}
